import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    business_name: "", owner_first_name: "", owner_last_name: "", email: "", password: "", phone: "",
  });
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(form);
      toast.success("Welcome to WindowIQX! Your 30-day trial starts now.");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-8 order-2 md:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-bold text-lg">WindowIQX</span>
          </Link>
          <h1 className="font-display text-3xl font-bold tracking-tight">Start your 30-day free trial</h1>
          <p className="text-muted-foreground mt-2 text-sm">No card needed. All features. Cancel anytime.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium">Business name</label>
              <input required value={form.business_name} onChange={change("business_name")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-business-name" placeholder="Sparkle Windows Ltd" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">First name</label>
                <input required value={form.owner_first_name} onChange={change("owner_first_name")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-first-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Last name</label>
                <input required value={form.owner_last_name} onChange={change("owner_last_name")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-last-name" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Work email</label>
              <input type="email" required value={form.email} onChange={change("email")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-email" />
            </div>
            <div>
              <label className="text-sm font-medium">Phone (optional)</label>
              <input type="tel" value={form.phone} onChange={change("phone")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-phone" placeholder="07... or +44 ..." />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="password" required value={form.password} onChange={change("password")} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="signup-password" minLength={6} />
              <div className="text-xs text-muted-foreground mt-1">Minimum 6 characters</div>
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-md bg-primary text-primary-foreground py-2.5 font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="signup-submit">
              {busy ? "Creating your business…" : <>Start free trial <ArrowRight className="h-4 w-4" /></>}
            </button>
            <div className="text-sm text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="link-to-login">Log in</Link>
            </div>
          </div>
        </form>
      </div>
      <div className="relative hidden md:block order-1 md:order-2">
        <img src="https://images.pexels.com/photos/15743361/pexels-photo-15743361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-foreground/30"></div>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-display text-3xl tracking-tight font-bold leading-tight">Fewer spreadsheets. More clean windows.</p>
          <p className="mt-3 opacity-90 text-sm">Set up your round in minutes. Built for UK window cleaners.</p>
        </div>
      </div>
    </div>
  );
}
